import * as React from "react";
import { Calendar as CalendarIcon, X } from "lucide-react";

import { cn } from "../../lib/utils";
import { inputBaseClass, inputSizeClass } from "./input";

export interface DateInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type" | "value" | "onChange"
> {
  value?: string;
  onChange?: (value: string | undefined) => void;
  includeTime?: boolean;
  inputSize?: "sm" | "default";
}

const DateInput = React.forwardRef<HTMLInputElement, DateInputProps>(
  (
    {
      value,
      onChange,
      placeholder = "Select date",
      disabled = false,
      className,
      includeTime = false,
      inputSize = "default",
      ...props
    },
    ref,
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e.target.value || undefined);
    };

    const handleClear = () => {
      onChange?.(undefined);
    };

    return (
      <div className={cn("relative", className)}>
        <CalendarIcon
          className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-text-tertiary"
          aria-hidden
        />
        <input
          ref={ref}
          type={includeTime ? "datetime-local" : "date"}
          value={value || ""}
          onChange={handleChange}
          disabled={disabled}
          placeholder={placeholder}
          className={cn(
            inputBaseClass,
            inputSizeClass[inputSize],
            "pl-7 pr-7",
            "[&::-webkit-calendar-picker-indicator]:cursor-pointer",
            "[&::-webkit-calendar-picker-indicator]:opacity-50",
            "dark:[&::-webkit-calendar-picker-indicator]:invert",
            !value && "text-text-tertiary",
          )}
          {...props}
        />
        {value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className={cn(
              "absolute right-1 top-1/2 grid size-5 -translate-y-1/2 place-items-center rounded-sm",
              "text-text-tertiary",
              "transition-[background-color,color] duration-100 ease-out-fast",
              "hover:bg-hover hover:text-text",
              "focus-visible:outline-none focus-fill",
            )}
          >
            <X className="size-3.5" aria-hidden />
            <span className="sr-only">Clear date</span>
          </button>
        )}
      </div>
    );
  },
);
DateInput.displayName = "DateInput";

export { DateInput };
